/* ===========================================================================
   cookbook-3option-waitlist.js
   International "Notify me" waitlist form for the cookbook-3option PDP.

   Vanilla, deferred, no libraries. Builder B (assets only).
   Codes against the HTML/JS contract in
   docs/site-build/cookbook-pdp-3option/intl-waitlist-prototype-spec.md
   (Builder A renders the section markup + <dialog> to the same contract).

   ---------------------------------------------------------------------------
   KLAVIYO CLIENT SUBSCRIPTIONS API (verified 2026-06-14)
   Endpoint:  POST https://a.klaviyo.com/client/subscriptions/?company_id=<companyId>
   Headers:   revision: 2026-04-15
              Content-Type: application/vnd.api+json
   Auth:      none (client-side, public company_id only). Double opt-in is
              configured ON the list, so Klaviyo sends the confirmation email;
              the profile is PENDING until the customer confirms.

   Doc URLs:
     https://developers.klaviyo.com/en/reference/create_client_subscription
     OpenAPI schema (canonical, used for the payload below):
     https://raw.githubusercontent.com/klaviyo/openapi/main/openapi/stable/apis/create_client_subscription.json

   Payload shape used (JSON:API). Custom properties + email consent both ride
   inline on the nested profile resource, so this is a single client call:

   {
     "data": {
       "type": "subscription",
       "attributes": {
         "custom_source": "Intl print waitlist (PDP)",
         "profile": {
           "data": {
             "type": "profile",
             "attributes": {
               "email": "<email>",
               "properties": {
                 "requested_format":  "<print|combo>",
                 "requested_book":    "<book title>",
                 "requested_country": "<ISO country>"
               },
               "subscriptions": {
                 "email": { "marketing": { "consent": "SUBSCRIBED" } }
               }
             }
           }
         }
       },
       "relationships": {
         "list": { "data": { "type": "list", "id": "<listId>" } }
       }
     }
   }
   =========================================================================== */

(function () {
  'use strict';

  var DIALOG_ID = 'shl-waitlist';
  var TRIGGER_SELECTOR = '.cookbook-3option__card--unavailable';
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-04-15';
  var SUCCESS_CLOSE_MS = 3000; // long enough for a screen reader to announce success

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  ready(function () {
    var dialog = document.getElementById(DIALOG_ID);
    if (!dialog) {
      // No unavailable cards on this page (e.g. US market). Nothing to wire.
      return;
    }

    // Idempotent wiring: the theme editor can re-run this script against an
    // already-wired dialog. Without this latch we would bind a second document
    // click handler and call showModal() on an open dialog (InvalidStateError).
    if (dialog.dataset.shlWaitlistWired) return;
    dialog.dataset.shlWaitlistWired = '1';

    var form = dialog.querySelector('.shl-waitlist__form');
    var msgEl = dialog.querySelector('.shl-waitlist__msg');
    var emailInput = dialog.querySelector('input[name="email"]');
    var fFormat = dialog.querySelector('input[name="requested_format"]');
    var fBook = dialog.querySelector('input[name="requested_book"]');
    var fCountry = dialog.querySelector('input[name="requested_country"]');
    var submitBtn = form ? form.querySelector('button[type="submit"]') : null;

    var supportsModal = typeof dialog.showModal === 'function';
    var lastTrigger = null;
    var submitting = false;   // in-flight guard (blocks Enter double-submit)
    var submitSeq = 0;        // request token: ignore stale responses
    var closeTimer = null;    // cancellable success auto-close

    function cancelCloseTimer() {
      if (closeTimer) { window.clearTimeout(closeTimer); closeTimer = null; }
    }
    function scheduleClose() {
      cancelCloseTimer();
      closeTimer = window.setTimeout(closeDialog, SUCCESS_CLOSE_MS);
    }

    // ---- open helpers ------------------------------------------------------

    function setMsg(text, kind) {
      if (!msgEl) return;
      msgEl.textContent = text || '';
      msgEl.classList.remove('shl-waitlist__msg--error', 'shl-waitlist__msg--ok');
      if (kind === 'error') msgEl.classList.add('shl-waitlist__msg--error');
      if (kind === 'ok') msgEl.classList.add('shl-waitlist__msg--ok');
    }

    function openDialog(trigger) {
      lastTrigger = trigger || document.activeElement;

      // Reopening invalidates any in-flight request + pending auto-close so a
      // stale response can't stomp the freshly opened form.
      cancelCloseTimer();
      submitSeq++;
      submitting = false;
      if (submitBtn) submitBtn.disabled = false;

      var format = trigger ? trigger.getAttribute('data-waitlist-format') : '';
      var book = trigger ? trigger.getAttribute('data-waitlist-book') : '';

      if (fFormat) fFormat.value = format || '';
      if (fBook) fBook.value = book || '';
      if (fCountry) fCountry.value = dialog.getAttribute('data-country') || '';

      setMsg('', null);
      if (form) form.classList.remove('is-submitting');

      if (supportsModal) {
        dialog.showModal();
      } else {
        // Fallback for browsers without <dialog>.showModal support.
        dialog.setAttribute('open', '');
        dialog.classList.add('is-open');
      }

      // Move focus into the form for keyboard + screen-reader users.
      if (emailInput) {
        try { emailInput.focus(); } catch (e) { /* ignore */ }
      }
    }

    function closeDialog() {
      cancelCloseTimer();
      if (supportsModal && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        dialog.classList.remove('is-open');
      }
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        try { lastTrigger.focus(); } catch (e) { /* ignore */ }
      }
      lastTrigger = null;
    }

    // ---- trigger delegation ------------------------------------------------

    document.addEventListener('click', function (evt) {
      var trigger = evt.target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;
      evt.preventDefault();
      openDialog(trigger);
    });

    // Esc + return-focus. Native <dialog> fires 'cancel' on Esc; handle the
    // fallback path manually so Esc still closes when showModal is unsupported.
    dialog.addEventListener('cancel', function (evt) {
      evt.preventDefault(); // run our own close so focus returns to the trigger
      closeDialog();
    });

    if (!supportsModal) {
      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && dialog.classList.contains('is-open')) {
          closeDialog();
        }
      });
    }

    // Click on the backdrop area (outside the form) closes the dialog. With
    // native <dialog> the element itself fills the viewport and the form is the
    // visible card, so a click whose target is the dialog (not the form) is a
    // backdrop click.
    dialog.addEventListener('click', function (evt) {
      if (evt.target === dialog) closeDialog();
    });

    // Minimal focus trap: keep Tab cycling inside the dialog while open.
    dialog.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Tab') return;
      var focusables = dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    });

    // ---- submit ------------------------------------------------------------

    function validEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    if (form) {
      form.addEventListener('submit', function (evt) {
        evt.preventDefault();
        if (submitting) return; // hard gate: blocks Enter-key double submit

        var email = emailInput ? emailInput.value.trim() : '';

        if (!validEmail(email)) {
          setMsg('Please enter a valid email address.', 'error');
          if (emailInput) emailInput.focus();
          return;
        }

        var listId = dialog.getAttribute('data-list-id') || '';
        var companyId = dialog.getAttribute('data-company-id') || '';

        // Dev guard: works before the Klaviyo list is created. No throw.
        if (!listId) {
          console.info(
            '[shl-waitlist] Klaviyo list not configured yet (data-list-id empty). ' +
            'Set intl_waitlist_list_id in the section settings. Skipping API call. Payload would be:',
            {
              email: email,
              requested_format: fFormat ? fFormat.value : '',
              requested_book: fBook ? fBook.value : '',
              requested_country: fCountry ? fCountry.value : ''
            }
          );
          setMsg("Thanks. We'll be in touch about print where you live.", 'ok');
          scheduleClose();
          return;
        }

        if (!companyId) {
          setMsg('Something went wrong. Please try again later.', 'error');
          console.warn('[shl-waitlist] Missing data-company-id on dialog.');
          return;
        }

        var payload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Intl print waitlist (PDP)',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    properties: {
                      requested_format: fFormat ? fFormat.value : '',
                      requested_book: fBook ? fBook.value : '',
                      requested_country: fCountry ? fCountry.value : ''
                    },
                    subscriptions: {
                      email: { marketing: { consent: 'SUBSCRIBED' } }
                    }
                  }
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: listId } }
            }
          }
        };

        submitting = true;
        var mySeq = ++submitSeq; // token: a reopen/new submit invalidates this one
        form.classList.add('is-submitting');
        if (submitBtn) submitBtn.disabled = true;
        setMsg('Adding you to the list...', null);

        fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            if (mySeq !== submitSeq) return; // stale: dialog was reopened
            // Klaviyo returns 202 Accepted on success (no body).
            if (res.ok || res.status === 202) {
              setMsg('Confirmation link sent to your email.', 'ok');
              if (form && typeof form.reset === 'function') form.reset();
              scheduleClose();
            } else {
              return res.text().then(function (body) {
                console.warn('[shl-waitlist] Klaviyo error', res.status, body);
                setMsg('Something went wrong. Please try again.', 'error');
              });
            }
          })
          .catch(function (err) {
            if (mySeq !== submitSeq) return; // stale
            console.warn('[shl-waitlist] Network error', err);
            setMsg('Could not reach the server. Please try again.', 'error');
          })
          .finally(function () {
            if (mySeq !== submitSeq) return; // a newer request owns the UI now
            submitting = false;
            form.classList.remove('is-submitting');
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    }
  });
})();
